import { Bitcoin, Shield, Globe, Map, Users, Heart } from 'lucide-react';
import { Link } from 'react-router-dom';
import PageMeta from '@/components/PageMeta';

const About = () => {
  return (
    <div className="min-h-screen bg-background">
      <PageMeta title="About" path="/about" description="OrangeLabel.io is a global, open-source directory of Bitcoin-accepting businesses and meetups, maintained by the Australian Bitcoin Industry Body." />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-16">
        <div className="mb-8 sm:mb-12">
          <h1 className="text-2xl sm:text-3xl md:text-4xl tracking-tight font-bold font-heading text-foreground mb-3 sm:mb-4" data-testid="about-heading">
            About Orange Label
          </h1>
          <p className="text-base sm:text-lg text-muted-foreground">
            Supporting a bitcoin standard — worldwide
          </p>
        </div>

        <div className="prose max-w-none space-y-6 sm:space-y-8">
          <div className="bg-muted border border-border rounded-xl p-5 sm:p-8">
            <h2 className="text-xl sm:text-2xl font-heading font-semibold text-foreground mb-3 sm:mb-4">Our Mission</h2>
            <p className="text-sm sm:text-base text-muted-foreground leading-relaxed mb-3 sm:mb-4">
              Orange Label is a global directory of businesses and meetup groups that accept or support Bitcoin.
              We connect Bitcoiners with merchants, service providers, and communities across five regions:
              <strong> Oceania, Asia, Europe, the Americas, and Africa</strong> — making it easier to live
              on a Bitcoin standard wherever you are in the world.
            </p>
            <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
              Whether you're looking to spend your sats on everyday goods and services, find a Bitcoin-friendly
              accountant in your country, or locate the nearest meetup group, Orange Label helps you discover
              the global circular economy built on Bitcoin.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6">
            <div className="bg-card border border-border rounded-xl p-5 sm:p-6">
              <Bitcoin className="w-8 sm:w-10 h-8 sm:h-10 text-primary mb-3 sm:mb-4" strokeWidth={1.5} />
              <h3 className="text-lg sm:text-xl font-heading font-semibold text-foreground mb-2">Circular Economy</h3>
              <p className="text-xs sm:text-sm text-muted-foreground">
                Supporting the growth of a global Bitcoin circular economy by connecting consumers with Bitcoin-accepting merchants worldwide
              </p>
            </div>

            <div className="bg-card border border-border rounded-xl p-5 sm:p-6">
              <Map className="w-8 sm:w-10 h-8 sm:h-10 text-primary mb-3 sm:mb-4" strokeWidth={1.5} />
              <h3 className="text-lg sm:text-xl font-heading font-semibold text-foreground mb-2">Global Directory</h3>
              <p className="text-xs sm:text-sm text-muted-foreground">
                Curated listings of service providers, retailers, and professionals accepting Bitcoin across all five UN regions
              </p>
            </div>

            <div className="bg-card border border-border rounded-xl p-5 sm:p-6 sm:col-span-2 md:col-span-1">
              <Globe className="w-8 sm:w-10 h-8 sm:h-10 text-primary mb-3 sm:mb-4" strokeWidth={1.5} />
              <h3 className="text-lg sm:text-xl font-heading font-semibold text-foreground mb-2">BTCMap Integration</h3>
              <p className="text-xs sm:text-sm text-muted-foreground">
                Physical locations sourced from BTCMap.org — mapped globally by a worldwide volunteer community
              </p>
            </div>
          </div>

          {/* BTCMap Credit Section */}
          <div className="bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800 rounded-xl p-5 sm:p-8">
            <div className="flex flex-col sm:flex-row items-start gap-4">
              <div className="flex-shrink-0">
                <img
                  src="/logos/btcmap.png"
                  alt="BTCMap"
                  className="h-10 sm:h-12 w-auto"
                  onError={(e) => { e.currentTarget.src = 'https://btcmap.org/images/logo.svg'; }}
                />
              </div>
              <div>
                <h2 className="text-xl sm:text-2xl font-heading font-semibold text-foreground mb-2 sm:mb-3">
                  Powered by BTCMap Volunteers
                </h2>
                <p className="text-sm sm:text-base text-muted-foreground leading-relaxed mb-3 sm:mb-4">
                  Physical business locations on Orange Label are sourced from{' '}
                  <a href="https://btcmap.org" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline font-medium">BTCMap.org</a>,
                  a free and open-source project maintained by dedicated volunteers around the world. Their tireless work
                  mapping Bitcoin-accepting merchants globally — across all five regions — makes projects like Orange Label possible.
                </p>
                <div className="flex items-start sm:items-center gap-2 text-xs sm:text-sm text-green-700 dark:text-green-400">
                  <Heart className="w-4 h-4 fill-current flex-shrink-0 mt-0.5 sm:mt-0" />
                  <span className="font-medium">Thank you to the BTCMap community for your incredible contribution to global Bitcoin adoption!</span>
                </div>
                <div className="mt-3 sm:mt-4 flex flex-wrap gap-2 sm:gap-3">
                  <a
                    href="https://btcmap.org"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-xs sm:text-sm bg-card border border-green-300 dark:border-green-700 text-green-700 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-900/50 rounded-full px-3 sm:px-4 py-1.5 sm:py-2 transition-colors"
                  >
                    <Globe className="w-3 sm:w-4 h-3 sm:h-4" />
                    Visit BTCMap
                  </a>
                  <a
                    href="https://github.com/teambtcmap"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-xs sm:text-sm bg-card border border-green-300 dark:border-green-700 text-green-700 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-900/50 rounded-full px-3 sm:px-4 py-1.5 sm:py-2 transition-colors"
                  >
                    <Users className="w-3 sm:w-4 h-3 sm:h-4" />
                    Contribute
                  </a>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-primary/10 border border-primary/20 rounded-xl p-5 sm:p-8">
            <div className="flex flex-col sm:flex-row items-start gap-4">
              <div className="flex-shrink-0">
                <a
                  href="https://bitcoinindustrybody.org.au"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="abib-logo-link"
                >
                  <img
                    src="/logos/abib-96.png"
                    alt="Australian Bitcoin Industry Body"
                    className="h-10 sm:h-12 w-auto"
                    data-testid="abib-logo"
                  />
                </a>
              </div>
              <div>
                <h2 className="text-xl sm:text-2xl font-heading font-semibold text-foreground mb-2 sm:mb-3">Origin & Steward</h2>
                <p className="text-sm sm:text-base text-muted-foreground leading-relaxed mb-3 sm:mb-4">
                  Orange Label was originated by the{' '}
                  <strong>Australian Bitcoin Industry Body (ABIB)</strong> to advance Bitcoin adoption in Australia,
                  and has since grown into a globally-operating directory covering Bitcoin businesses and communities
                  across all five regions. ABIB continues to steward the project as it expands worldwide.
                </p>
                <div className="mt-3 sm:mt-4 flex flex-wrap gap-2 sm:gap-3">
                  <a
                    href="https://bitcoinindustrybody.org.au/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-xs sm:text-sm bg-card border border-primary/30 text-foreground hover:bg-primary/10 rounded-full px-3 sm:px-4 py-1.5 sm:py-2 transition-colors"
                  >
                    <Globe className="w-3 sm:w-4 h-3 sm:h-4" />
                    Visit ABIB Website
                  </a>
                  <a
                    href="https://bitcoinindustrybody.org.au/bitcoiners"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-xs sm:text-sm bg-card border border-primary/30 text-foreground hover:bg-primary/10 rounded-full px-3 sm:px-4 py-1.5 sm:py-2 transition-colors"
                  >
                    <Shield className="w-3 sm:w-4 h-3 sm:h-4" />
                    Take Action
                  </a>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-muted border border-border rounded-xl p-5 sm:p-8">
            <h2 className="text-xl sm:text-2xl font-heading font-semibold text-foreground mb-3 sm:mb-4">Get Involved</h2>
            <p className="text-sm sm:text-base text-muted-foreground leading-relaxed mb-4">
              Are you a business that accepts Bitcoin? We'd love to have you in our directory.
              Join the growing global community of Bitcoin merchants — from Oceania to Africa and everywhere in between.
            </p>
            <Link
              to="/submit"
              className="inline-block bg-primary text-white hover:bg-primary-hover rounded-full px-6 sm:px-8 py-2.5 sm:py-3 font-semibold transition-all shadow-sm hover:shadow-md text-sm sm:text-base"
              data-testid="get-involved-btn"
            >
              Submit Your Business
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default About;
